<script setup>
import { ChevronDown, ChevronUp } from 'lucide-vue-next'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Button,
} from '@codinglabsau/gooey'
import { computed } from 'vue'
import { useNavigation } from '../../../Utils/useNavigation.js'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  isOpen: {
    type: Boolean,
    default: false
  },
  level: {
    type: Number,
    default: 0
  },
})

const emit = defineEmits(['update:isOpen'])

const { getHref } = useNavigation()

const modelValue = defineModel('isOpen')

const itemIcon = computed(() => {
  if (props.item.hasIcon && props.item.iconComponent) {
    return props.item.iconComponent;
  }
  return null;
});

const chevronComponent = computed(() => {
  return modelValue.value ? ChevronUp : ChevronDown;
});
</script>

<template>
  <Collapsible
    v-model:open="modelValue"
    class="not-prose"
  >
    <!-- Item with href -->
    <template v-if="item.href">
      <div class="flex items-center px-4 py-1 justify-between hover:underline cursor-pointer" :class="{ 'bg-gray-100': modelValue }">
        <a :href="getHref(item.href)" class="flex-grow">
          <div class="flex items-center">
            <Component
              v-if="itemIcon"
              :is="itemIcon"
              class="size-4 mr-1"
            />
            <span class="whitespace-nowrap font-normal sm:font-medium sm:text-sm">{{ item.name }}</span>
          </div>
        </a>
        <CollapsibleTrigger as-child>
          <Button variant="ghost" size="sm" class="w-9 p-0">
            <Component :is="chevronComponent" class="h-4 w-4" />
            <span class="sr-only">Toggle</span>
          </Button>
        </CollapsibleTrigger>
      </div>
    </template>

    <!-- Item without href -->
    <CollapsibleTrigger v-else class="w-full">
      <div class="flex items-center px-4 py-1 justify-between hover:underline cursor-pointer" :class="{ 'bg-gray-100': modelValue }">
        <div class="flex items-center">
          <Component
            v-if="itemIcon"
            :is="itemIcon"
            class="size-4 mr-1"
          />
          <span class="whitespace-nowrap font-normal sm:font-medium sm:text-sm">{{ item.name }}</span>
        </div>
        <div>
          <Component :is="chevronComponent" class="h-4 w-4" />
        </div>
      </div>
    </CollapsibleTrigger>

    <CollapsibleContent class="pl-[1.5rem]">
      <slot></slot>
    </CollapsibleContent>
  </Collapsible>
</template>
